// Platform roles & permissions — reuses the same engine as the org-level Phase 1.5
// but lists all built-in platform roles + the categories Super Admin can assign.
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";

const PLATFORM_ROLES = [
  { name: "Platform Administrator", perms: "*" },
  { name: "Technical Support", perms: "organizations.read · audit.view · support.respond · sessions.revoke" },
  { name: "Finance Admin", perms: "billing.* · subscriptions.* · refunds.approve · reports.financial" },
  { name: "Sales Head", perms: "organizations.create · plans.assign · reports.sales" },
  { name: "Customer Success Manager", perms: "organizations.read · tickets.respond · usage.read" },
  { name: "AI Operations", perms: "ai.models · ai.prompts · ai.credits · ai.usage" },
  { name: "DevOps", perms: "monitoring.* · integrations.* · settings.maintenance · security.alerts" },
];

const CATEGORIES = ["Organizations", "Billing", "Marketplace", "AI", "Reports", "Settings", "Security", "Audit Logs"];

export default function PlatformRoles() {
  const [catalog, setCatalog] = useState(null);
  useEffect(() => { api.get("/permissions/catalog").then(({ data }) => setCatalog(data.catalog || data)).catch(() => {}); }, []);

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6" data-testid="admin-platform-roles">
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-black">Roles & permissions</h1>
        <p className="text-muted-foreground">Platform-level roles used by Projexino HQ staff. Org-level roles are managed by Org Admins inside each tenant.</p>
      </div>

      <Card>
        <CardHeader><CardTitle>Built-in platform roles</CardTitle></CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-3">
            {PLATFORM_ROLES.map((r) => (
              <div key={r.name} className="border rounded-md p-3" data-testid={`platform-role-${r.name.replace(/\s+/g, '-')}`}>
                <div className="flex items-center justify-between">
                  <div className="font-semibold">{r.name}</div>
                  <Badge variant="outline" className="text-[10px]">System</Badge>
                </div>
                <div className="font-mono text-[11px] text-muted-foreground mt-1">{r.perms}</div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Permission categories</CardTitle></CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {CATEGORIES.map((c) => <Badge key={c} variant="outline" className="text-xs">{c}</Badge>)}
          </div>
          <p className="text-xs text-muted-foreground mt-3">Custom platform roles can be created via the same engine the Org Admin uses (POST /api/roles with platform-scoped permission keys). Detailed UI lives at <span className="font-mono">/org/roles</span>; this page summarises the platform-wide defaults.</p>
        </CardContent>
      </Card>

      {catalog && (
        <Card>
          <CardHeader><CardTitle>Module permission catalog (shared with org roles)</CardTitle></CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-3 text-sm">
              {Object.entries(catalog).map(([mod, acts]) => (
                <div key={mod}><span className="font-semibold capitalize">{mod}:</span> <span className="font-mono text-xs text-muted-foreground">{acts.join(", ")}</span></div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
